import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { SummaryApi } from 'api/SummaryApi';
import { OpinionQuestionApi } from 'api/OpinionQuestionApi';
import { CompareGroupApi } from 'api/CompareGroupApi';
import { EngagementScoreSurvey } from 'model/EngagementScoreSurvey';
import { BenchmarkApi } from 'api/BenchmarkApi';
import { Summary } from 'model/Summary';

import { EngagementScoreMapper } from '../mapper/EngagementScoreMapper';
import { SummaryMapper } from '../mapper/SummaryMapper';

export namespace ProjectScoreApi {
  export type EntryCombine = {
    byCategory: OpinionQuestionApi.CombineEntry[];
    showImpactOnEngagement: boolean;
  };

  export type EngagementCombine = ProjectScoreApi.EntryCombine & {
    summary: SummaryApi.EntryCombine;
    chartLabel?: string;
    aggregatedBenchmark?: BenchmarkApi.Entry;
  };

  export type PulseSummaryEntry = {
    byProject: SummaryApi.ProjectSummary[];
    aggregatedBenchmark: BenchmarkApi.Entry;
  };

  export type EntryCompare = {
    summary: SummaryApi.EntryCompare[];
    categories: OpinionQuestionApi.CompareEntry[];
    groups: CompareGroupApi.Entry;
    combineModeLock: boolean;
    protections: { [key: number]: boolean };
  };
}

export class ProjectScoreApi {
  static isEngagementCombine = (score: unknown): score is ProjectScoreApi.EngagementCombine => {
    return !!(score as ProjectScoreApi.EngagementCombine).summary;
  };

  constructor(
    private client: AxiosInstance,
    private engagementScoreMapper: EngagementScoreMapper,
    private summaryMapper: SummaryMapper
  ) {}

  combine(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<EngagementScoreSurvey.Combine | void> {
    return this.client
      .get<ProjectScoreApi.EntryCombine | ProjectScoreApi.EngagementCombine>(
        `/api/v1/project/${projectId}/report/summary/combined${query}`,
        { signal }
      )
      .then((response) => this.engagementScoreMapper.deserializeCombine(response.data))
      .catch((error) => {
        if (signal?.aborted === false) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }

  compare(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<EngagementScoreSurvey.Compare | void> {
    return this.client
      .get(`/api/v1/project/${projectId}/report/summary/compared${query}`, { signal })
      .then((response) => this.engagementScoreMapper.deserializeCompare(response.data))
      .catch((error) => {
        if (signal?.aborted === false) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }

  pulsesSummary(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<Summary.Pulses | void> {
    return this.client
      .get<ProjectScoreApi.PulseSummaryEntry>(
        `/api/v1/project/${projectId}/report/pulse-summary${query}`,
        { signal }
      )
      .then(({ data: { byProject, aggregatedBenchmark } }) =>
        this.summaryMapper.deserializePulseSummary(aggregatedBenchmark, byProject)
      )
      .catch((error) => {
        if (signal?.aborted === false) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }
}
